import React, { useState, useEffect } from 'react';
import { Alert, Form, Button, Collapse, Card, FilterGrid, FilterField } from '@/components/ui';
import reportService from '../../../services/reportService';
import ReportParametersCard from '../components/ReportParametersCard';
import ReportPreviewTable from '../components/ReportPreviewTable';
import ExportOptionsModal from '../components/ExportOptionsModal';
import YellowHeaderInfo from '../components/YellowHeaderInfo';
import Toast from '../../../components/Toast';
import { ServiceArea } from '../../../services/areasService';
import { employeeOptionName, useEmployeeOptions } from '../utils/reportFilterOptions';

type WeeklyPayrollPreviewRow = {
  id: string;
  'Employee Number': string;
  'Employee Name': string;
  Date: string;
  Shift: string;
  Department: string;
  Position: string;
  'Start Time': string;
  'End Time': string;
  'Total Hours': string;
  Earnings: string;
};

const PREVIEW_COLUMNS = [
  { label: 'Employee Number', key: 'Employee Number', sortable: false, width: '120px' },
  { label: 'Employee Name', key: 'Employee Name', sortable: false, width: '160px' },
  { label: 'Date', key: 'Date', sortable: false, width: '100px' },
  { label: 'Shift', key: 'Shift', sortable: false, width: '80px' },
  { label: 'Department', key: 'Department', sortable: false, width: '120px' },
  { label: 'Position', key: 'Position', sortable: false, width: '160px' },
  { label: 'Start', key: 'Start Time', sortable: false, width: '70px' },
  { label: 'End', key: 'End Time', sortable: false, width: '70px' },
  { label: 'Hours', key: 'Total Hours', sortable: false, width: '70px' },
  { label: 'Earnings', key: 'Earnings', sortable: false, width: '100px' }
];

interface WeeklyPayrollReportViewProps {
  serviceAreas: ServiceArea[];
  canCreateWeeklyPayroll: boolean;
  loadingServiceAreas: boolean;
  defaultServiceAreaId?: string;
}

const WeeklyPayrollReportView: React.FC<WeeklyPayrollReportViewProps> = ({
  serviceAreas,
  canCreateWeeklyPayroll,
  loadingServiceAreas,
  defaultServiceAreaId
}) => {
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    serviceArea: defaultServiceAreaId || '',
    employeeName: '',
    exportStatus: 'unexported' as 'unexported' | 'exported' | 'all'
  });
  const [previewData, setPreviewData] = useState<WeeklyPayrollPreviewRow[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
    onPageChange: (page: number, pageSize?: number) => {
      void page;
      void pageSize;
    }
  });
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingPreliminary, setLoadingPreliminary] = useState(false);
  const [loadingFinal, setLoadingFinal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(false);
  const [showAdditionalFilters, setShowAdditionalFilters] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [dateFilterType, setDateFilterType] = useState<'approval' | 'formDate'>('approval');

  const clearPreview = () => {
    setPreviewData([]);
    setPagination((prev) => ({ ...prev, total: 0, totalPages: 0 }));
  };

  const handleDateFilterTypeChange = (value: 'approval' | 'formDate') => {
    setDateFilterType(value);
    clearPreview();
  };

  const employeeOptions = useEmployeeOptions(filters.serviceArea);

  useEffect(() => {
    if (defaultServiceAreaId && !filters.serviceArea) {
      setFilters((f) => ({ ...f, serviceArea: defaultServiceAreaId }));
    }
  }, [defaultServiceAreaId]);

  // Clear employee selection when it falls outside the selected service area.
  useEffect(() => {
    setFilters((f) => {
      const employeeValid =
        !f.employeeName || employeeOptions.some((emp) => employeeOptionName(emp) === f.employeeName);
      if (employeeValid) return f;
      return { ...f, employeeName: '' };
    });
  }, [employeeOptions]);

  const handlePageChange = (page: number, pageSize?: number) => {
    void handleGeneratePreview(page, pageSize);
  };

  const handleGeneratePreview = async (page: number = 1, pageSize?: number) => {
    setLoadingPreview(true);
    setErrorMessage(null);
    try {
      const res = await reportService.getWeeklyPayrollPreview({
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        dateFilterType,
        serviceArea: filters.serviceArea || undefined,
        employeeName: filters.employeeName || undefined,
        exportStatus: filters.exportStatus,
        page,
        limit: pageSize || pagination.limit
      });
      const limit = res.pagination.limit;
      const pageNum = res.pagination.page;
      const rows: WeeklyPayrollPreviewRow[] = (res.data as Record<string, string>[]).map((row, i) => ({
        id: `${pageNum}-${i}`,
        'Employee Number': String(row['Employee Number'] ?? ''),
        'Employee Name': String(row['Employee Name'] ?? ''),
        Date: String(row.Date ?? ''),
        Shift: String(row.Shift ?? ''),
        Department: String(row.Department ?? ''),
        Position: String(row.Position ?? ''),
        'Start Time': String(row['Start Time'] ?? ''),
        'End Time': String(row['End Time'] ?? ''),
        'Total Hours': String(row['Total Hours'] ?? ''),
        Earnings: String(row.Earnings ?? '')
      }));
      setPreviewData(rows);
      setPagination({
        page: res.pagination.page,
        limit,
        total: res.pagination.total,
        totalPages: res.pagination.totalPages,
        onPageChange: handlePageChange
      });
    } catch {
      setErrorMessage('Failed to load weekly payroll preview.');
      setPreviewData([]);
    } finally {
      setLoadingPreview(false);
    }
  };

  const exportReport = async (dryRun: boolean) => {
    if (!canCreateWeeklyPayroll) return;
    setErrorMessage(null);
    try {
      await reportService.exportWeeklyPayrollReport({
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        dateFilterType,
        serviceArea: filters.serviceArea || undefined,
        employeeName: filters.employeeName || undefined,
        exportStatus: filters.exportStatus,
        dryRun
      });
      setToastMessage(
        dryRun
          ? 'Preliminary report downloaded (forms not marked exported).'
          : 'Weekly payroll exported.'
      );
      setShowToast(true);
      setShowExportModal(false);
      await handleGeneratePreview(pagination.page, pagination.limit);
    } catch {
      setErrorMessage('Failed to export weekly payroll report.');
    }
  };

  const handlePreliminaryExport = async () => {
    setLoadingPreliminary(true);
    try {
      await exportReport(true);
    } finally {
      setLoadingPreliminary(false);
    }
  };

  const handleFinalizedExport = async () => {
    setLoadingFinal(true);
    try {
      await exportReport(false);
    } finally {
      setLoadingFinal(false);
    }
  };
  const tableColumns = PREVIEW_COLUMNS.map((c) => ({
    ...c,
    render: (row: WeeklyPayrollPreviewRow) => String(row[c.key as keyof WeeklyPayrollPreviewRow] ?? '')
  }));

  const handlePageSizeChange = (newPageSize: number) => {
    void handleGeneratePreview(1, newPageSize);
  };

  return (
    <div className="tw-flex tw-flex-col" style={{ height: '100%', minHeight: 0 }}>
      <h3 className="tw-mb-3">Payroll Report</h3>
      {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}

      <ReportParametersCard
        onGenerate={() => void handleGeneratePreview(1)}
        onExport={() => {}}
        loadingGenerate={loadingPreview}
        loadingExport={false}
        disableExport={true}
        generateButtonText="Run Report"
        hideExportButton={true}
        defaultCollapsed={false}
        onCollapseChange={setIsFiltersCollapsed}
      >
        <FilterGrid min="lg">
          <Form.Group className="tw-mb-1">
            <Form.Label className="tw-text-sm tw-mb-1">Service Area</Form.Label>
            <Form.Select
              size="sm"
              value={filters.serviceArea}
              onChange={(e) => setFilters((f) => ({ ...f, serviceArea: e.target.value }))}
              disabled={loadingServiceAreas}
            >
              <option value="">All</option>
              {serviceAreas.map((a) => (
                <option key={a.id} value={String(a.id)}>
                  {a.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <FilterField span={2}>
            <Form.Group className="tw-mb-1">
              <Form.Label className="tw-text-sm tw-mb-1">Date range</Form.Label>
              <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
                <div className="tw-flex tw-items-center tw-gap-2">
                  <span className="tw-text-content-muted tw-text-sm">Filter by:</span>
                  <Form.Check
                    type="radio"
                    id="payroll-dateFilter-approval"
                    name="payrollDateFilterType"
                    label="Approval date"
                    checked={dateFilterType === 'approval'}
                    onChange={() => handleDateFilterTypeChange('approval')}
                    className="tw-text-sm"
                  />
                  <Form.Check
                    type="radio"
                    id="payroll-dateFilter-formDate"
                    name="payrollDateFilterType"
                    label="Form date"
                    checked={dateFilterType === 'formDate'}
                    onChange={() => handleDateFilterTypeChange('formDate')}
                    className="tw-text-sm"
                  />
                </div>
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                  <Form.Control
                    size="sm"
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => setFilters((f) => ({ ...f, startDate: e.target.value }))}
                    style={{ width: '140px' }}
                  />
                  <span className="tw-text-content-muted tw-text-sm">to</span>
                  <Form.Control
                    size="sm"
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => setFilters((f) => ({ ...f, endDate: e.target.value }))}
                    style={{ width: '140px' }}
                  />
                </div>
              </div>
              <Form.Text className="tw-text-content-muted">
                {dateFilterType === 'approval'
                  ? 'Includes forms approved in this range; row dates reflect work performed.'
                  : 'Includes forms whose form date falls in this range; forms without a form date are excluded.'}
              </Form.Text>
            </Form.Group>
          </FilterField>
        </FilterGrid>

        <div className="tw-mt-3">
          <Button
            variant="link"
            size="sm"
            onClick={() => setShowAdditionalFilters(!showAdditionalFilters)}
            className="tw-p-0 tw-no-underline"
            style={{ fontSize: '0.875rem' }}
          >
            <i className={`bi bi-chevron-${showAdditionalFilters ? 'up' : 'down'} tw-mr-1`}></i>
            Additional Filters
          </Button>
          <Collapse in={showAdditionalFilters}>
            <div>
              <FilterGrid className="tw-mt-2">
                <Form.Group className="tw-mb-1">
                  <Form.Label className="tw-text-sm tw-mb-1">Export status</Form.Label>
                  <Form.Select
                    size="sm"
                    value={filters.exportStatus}
                    onChange={(e) =>
                      setFilters((f) => ({
                        ...f,
                        exportStatus: e.target.value as 'unexported' | 'exported' | 'all'
                      }))
                    }
                  >
                    <option value="unexported">Unexported</option>
                    <option value="exported">Exported</option>
                    <option value="all">All</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="tw-mb-1">
                  <Form.Label className="tw-text-sm tw-mb-1">Employee name</Form.Label>
                  <Form.Select
                    size="sm"
                    value={filters.employeeName}
                    onChange={(e) => setFilters((f) => ({ ...f, employeeName: e.target.value }))}
                  >
                    <option value="">All</option>
                    {employeeOptions.map((emp) => (
                      <option key={emp.id} value={employeeOptionName(emp)}>
                        {employeeOptionName(emp)}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </FilterGrid>
            </div>
          </Collapse>
        </div>
      </ReportParametersCard>

      <YellowHeaderInfo columnLabel="Employee Name" />

      <div className="tw-grow tw-mt-2" style={{ minHeight: 0, minWidth: 0, overflow: 'auto' }}>
        <Card style={{ display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0 }}>
          <Card.Header className="tw-flex tw-justify-between tw-items-center">
            <h6 className="tw-mb-0">Report Data ({pagination.total} records)</h6>
            {pagination.total > 0 && (
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => setShowExportModal(true)}
                disabled={!canCreateWeeklyPayroll}
              >
                <i className="bi bi-download tw-mr-1"></i>
                Export Report
              </Button>
            )}
          </Card.Header>
          <Card.Body className="tw-p-0" style={{ flex: 1, minHeight: 0 }}>
            <ReportPreviewTable<WeeklyPayrollPreviewRow>
              columns={tableColumns}
              data={previewData}
              loading={loadingPreview}
              yellowHeaderColumn="Employee Name"
              pagination={{
                page: pagination.page,
                limit: pagination.limit,
                total: pagination.total,
                totalPages: pagination.totalPages,
                onPageChange: handlePageChange,
                onPageSizeChange: handlePageSizeChange
              }}
              isFiltersCollapsed={isFiltersCollapsed}
              emptyMessage="No data available. Click 'Run Report' to load data."
            />
          </Card.Body>
        </Card>
      </div>

      <ExportOptionsModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onPreliminaryExport={() => void handlePreliminaryExport()}
        onFinalExport={() => void handleFinalizedExport()}
        loadingPreliminary={loadingPreliminary}
        loadingFinal={loadingFinal}
        isPayroll={true}
        size="small"
      />
      <Toast type="success" message={toastMessage} show={showToast} onClose={() => setShowToast(false)} />
    </div>
  );
};

export default WeeklyPayrollReportView;
